import type { InputState } from '@/shared/types';

interface TouchButton {
  element: HTMLButtonElement;
  action: keyof InputState;
}

const DPAD_ACTIONS: { label: string; action: keyof InputState; gridArea: string }[] = [
  { label: '\u25B2', action: 'up', gridArea: '1 / 2' },
  { label: '\u25C0', action: 'left', gridArea: '2 / 1' },
  { label: '\u25B6', action: 'right', gridArea: '2 / 3' },
  { label: '\u25BC', action: 'down', gridArea: '3 / 2' },
];

const ACTION_BUTTONS: { label: string; action: keyof InputState; color: string }[] = [
  { label: 'BITE', action: 'attack1', color: '#cc3333' },
  { label: 'TAIL', action: 'attack2', color: '#cc8833' },
  { label: 'SPEC', action: 'special', color: '#3388cc' },
  { label: 'BLK', action: 'block', color: '#33aa33' },
];

export class TouchControls {
  private container: HTMLDivElement;
  private buttons: TouchButton[] = [];
  private state: InputState = {
    left: false,
    right: false,
    up: false,
    down: false,
    attack1: false,
    attack2: false,
    special: false,
    block: false,
  };

  private visible = false;

  constructor() {
    this.container = document.createElement('div');
    this.container.id = 'touch-controls';
    this.container.style.cssText =
      'display:none;position:fixed;bottom:0;left:0;right:0;' +
      'height:160px;z-index:100;pointer-events:none;' +
      'padding:8px 12px;box-sizing:border-box;' +
      'justify-content:space-between;align-items:end;';

    const dpad = this.createDpad();
    const actions = this.createActionButtons();

    this.container.appendChild(dpad);
    this.container.appendChild(actions);
    document.body.appendChild(this.container);
  }

  private createDpad(): HTMLDivElement {
    const dpad = document.createElement('div');
    dpad.style.cssText =
      'display:grid;grid-template-columns:repeat(3,52px);' +
      'grid-template-rows:repeat(3,52px);gap:4px;pointer-events:auto;';

    for (const btn of DPAD_ACTIONS) {
      const el = this.makeButton(btn.label, '#555555');
      el.style.gridArea = btn.gridArea;
      el.style.fontSize = '22px';
      this.bindTouch(el, btn.action);
      dpad.appendChild(el);
      this.buttons.push({ element: el, action: btn.action });
    }

    return dpad;
  }

  private createActionButtons(): HTMLDivElement {
    const wrap = document.createElement('div');
    wrap.style.cssText =
      'display:grid;grid-template-columns:repeat(2,68px);' +
      'grid-template-rows:repeat(2,52px);gap:6px;pointer-events:auto;';

    for (const btn of ACTION_BUTTONS) {
      const el = this.makeButton(btn.label, btn.color);
      el.style.fontSize = '13px';
      el.style.fontWeight = 'bold';
      this.bindTouch(el, btn.action);
      wrap.appendChild(el);
      this.buttons.push({ element: el, action: btn.action });
    }

    return wrap;
  }

  private makeButton(label: string, bg: string): HTMLButtonElement {
    const el = document.createElement('button');
    el.textContent = label;
    el.style.cssText =
      `background:${bg};color:#fff;border:2px solid rgba(255,255,255,0.3);` +
      'border-radius:10px;font-family:monospace;' +
      'width:100%;height:100%;touch-action:none;' +
      'user-select:none;-webkit-user-select:none;' +
      'opacity:0.75;outline:none;cursor:pointer;';
    return el;
  }

  private bindTouch(el: HTMLButtonElement, action: keyof InputState): void {
    const press = (e: Event) => {
      e.preventDefault();
      this.state[action] = true;
      (e.target as HTMLButtonElement).style.opacity = '1';
    };
    const release = (e: Event) => {
      e.preventDefault();
      this.state[action] = false;
      (e.target as HTMLButtonElement).style.opacity = '0.75';
    };

    el.addEventListener('touchstart', press, { passive: false });
    el.addEventListener('touchend', release, { passive: false });
    el.addEventListener('touchcancel', release, { passive: false });

    el.addEventListener('mousedown', press);
    el.addEventListener('mouseup', release);
    el.addEventListener('mouseleave', release);

    el.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  show(): void {
    if (this.visible) return;
    this.visible = true;
    this.container.style.display = 'flex';
  }

  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    this.container.style.display = 'none';
    this.resetState();
  }

  isVisible(): boolean {
    return this.visible;
  }

  getState(): InputState {
    return { ...this.state };
  }

  consumeAttacks(): { attack1: boolean; attack2: boolean; special: boolean } {
    const attacks = {
      attack1: this.state.attack1,
      attack2: this.state.attack2,
      special: this.state.special,
    };
    this.state.attack1 = false;
    this.state.attack2 = false;
    this.state.special = false;
    return attacks;
  }

  private resetState(): void {
    this.state.left = false;
    this.state.right = false;
    this.state.up = false;
    this.state.down = false;
    this.state.attack1 = false;
    this.state.attack2 = false;
    this.state.special = false;
    this.state.block = false;
  }

  destroy(): void {
    this.container.remove();
  }
}
